import { useMemo, useState } from "react";
import { countryHref, learnHref } from "utils/learnPaths";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { WorldMap } from "./WorldMap";
import EnglishData from "utils/EnglishData.json";
import {
  Continent,
  CONTINENT_LABELS,
  FILTERABLE_CONTINENTS,
  parsedCountryName,
  resolveContinent,
  switchLanguageAria,
  switchLanguageLabel,
} from "utils/functions";
import { AvailableLanguages, Country } from "utils/types";

interface MobileExplorerProps {
  data: Country[];
  language: AvailableLanguages;
  onSwitchLanguage: () => void;
}

const copy = {
  [AvailableLanguages.EN]: {
    searchLabel: "Search a country",
    searchPlaceholder: "Search a country…",
    allContinents: "All",
    mapHint: "Pick a continent to zoom the map, or tap a country.",
    // "& territories" because the list includes the ten entries the dataset
    // flags as not sovereign (the Falklands, Western Sahara, Taiwan...).
    countriesFound: (n: number) =>
      n === 1 ? "1 result" : `${n} countries & territories`,
    noMatches: "No country matches that search.",
    learn: "Learn",
    quiz: "Self-quiz",
  },
  [AvailableLanguages.ES]: {
    searchLabel: "Buscar un país",
    searchPlaceholder: "Buscar un país…",
    allContinents: "Todos",
    mapHint: "Elegí un continente para acercar el mapa, o tocá un país.",
    countriesFound: (n: number) =>
      n === 1 ? "1 resultado" : `${n} países y territorios`,
    noMatches: "Ningún país coincide con esa búsqueda.",
    learn: "Aprender",
    quiz: "Autotest",
  },
};

const Wrap = styled.div`
  position: relative;
  z-index: 2;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 14px 24px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const Brand = styled.h1`
  margin: 0;
  font-family: "Fascinate Inline", cursive;
  font-size: 1.9rem;
  line-height: 1;
  background: -webkit-linear-gradient(#f5e2c8, #d88373);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const GhostButton = styled.button`
  cursor: pointer;
  flex: 0 0 auto;
  border: 1px solid rgba(245, 226, 200, 0.35);
  color: #f5e2c8;
  background: rgba(27, 38, 79, 0.3);
  padding: 8px 12px;
  border-radius: 999px;
  font-family: "Finger Paint", cursive;
  font-size: 0.72rem;

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(245, 226, 200, 0.3);
  background: rgba(27, 38, 79, 0.35);
  color: #f5e2c8;
  font-family: "Gaegu", cursive;
  font-size: 1.25rem;

  &::placeholder {
    color: rgba(245, 226, 200, 0.55);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

// Horizontally scrollable so seven continents fit a 375px screen without
// wrapping into three rows and pushing the map below the fold.
const ChipsRow = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Chip = styled.button<{ $active: boolean }>`
  cursor: pointer;
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 7px 13px;
  white-space: nowrap;
  font-family: "Finger Paint", cursive;
  font-size: 0.75rem;
  color: ${({ $active }) => ($active ? "#182848" : "#f5e2c8")};
  background: ${({ $active }) =>
    $active ? "rgba(173, 216, 230, 0.9)" : "rgba(27, 38, 79, 0.35)"};
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(173, 216, 230, 0.95)" : "rgba(245, 226, 200, 0.3)"};

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

// The map keeps a real, tappable presence on the phone instead of being
// replaced by a list: cropped to one continent it renders at a usable scale.
const MapPanel = styled.div`
  position: relative;
  height: 34vh;
  min-height: 220px;
  border-radius: 18px;
  border: 1px solid rgba(245, 226, 200, 0.18);
  background: rgba(27, 38, 79, 0.22);
  overflow: hidden;
`;

const Hint = styled.p`
  margin: 0;
  font-family: "Gaegu", cursive;
  font-size: 1rem;
  color: rgba(245, 226, 200, 0.7);
`;

const ListCount = styled.p`
  margin: 0;
  font-family: "Finger Paint", cursive;
  font-size: 0.75rem;
  letter-spacing: 0.2px;
  color: rgba(245, 226, 200, 0.6);
`;

const CountryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;

  /* The list item is the grid item and stretches to the row on its own, but
     the link inside it kept its own content height — so next to a name that
     wraps to two lines ("Bosnia y Herzegovina"), a one-line card floated
     with a gap beneath it. Making the item a flex box hands its full height
     down to the link. */
  & > li {
    display: flex;
  }
`;

const CountryRow = styled(Link)`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 10px;
  padding: 9px 11px;
  border-radius: 14px;
  border: 1px solid rgba(245, 226, 200, 0.16);
  background: rgba(27, 38, 79, 0.28);
  color: #f5e2c8;
  text-decoration: none;
  font-family: "Gaegu", cursive;
  font-size: 1.15rem;
  line-height: 1.15;

  &:active {
    background: rgba(27, 38, 79, 0.5);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const RowFlag = styled.img`
  width: 32px;
  height: 22px;
  flex: 0 0 auto;
  border-radius: 4px;
  border: 1px solid rgba(245, 226, 200, 0.25);
  background: rgba(0, 0, 0, 0.2);
  object-fit: cover;
`;

const Empty = styled.p`
  margin: 6px 0;
  font-family: "Gaegu", cursive;
  font-size: 1.15rem;
  color: rgba(245, 226, 200, 0.8);
`;

const FooterNav = styled.nav`
  position: sticky;
  bottom: 0;
  display: flex;
  gap: 10px;
  padding-top: 10px;
  margin-top: auto;
`;

const NavLink = styled(Link)`
  flex: 1 1 0;
  text-align: center;
  padding: 12px 10px;
  border-radius: 14px;
  border: 1px solid rgba(245, 226, 200, 0.35);
  background: rgba(27, 38, 79, 0.75);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: #f5e2c8;
  font-family: "Finger Paint", cursive;
  font-size: 0.85rem;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

export const MobileExplorer = ({
  data,
  language,
  onSwitchLanguage,
}: MobileExplorerProps) => {
  const [query, setQuery] = useState("");
  const [continent, setContinent] = useState<Continent | undefined>(undefined);
  const t = copy[language];
  const isSpanish = language === AvailableLanguages.ES;
  const continentLabels = CONTINENT_LABELS[language];

  const countries = useMemo(
    () =>
      data
        // Deliberately no "not-a-country" filter here. That flag is right for
        // the quiz (it shouldn't ask for the capital of Western Sahara as
        // though it were sovereign), but this is a browser: those ten
        // territories are drawn on the map, have their own pages, and the
        // desktop search finds them. Filtering them out made the Falklands,
        // Greenland, Puerto Rico, Taiwan and the rest tappable on the map but
        // unreachable from the list or the search box.
        .map((country) => {
          const englishName = country["english-name"];
          const rawContinent = (
            EnglishData as Record<string, { Continent?: string }>
          )[englishName]?.Continent;
          return {
            country,
            displayName: isSpanish
              ? country["spanish-name"]
              : country["english-name"],
            continent: resolveContinent(englishName, rawContinent),
          };
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [data, isSpanish]
  );

  const visibleCountries = useMemo(() => {
    const q = parsedCountryName(query).trim();
    return countries.filter((entry) => {
      if (continent && entry.continent !== continent) return false;
      if (!q) return true;
      return (
        parsedCountryName(entry.country["english-name"]).includes(q) ||
        parsedCountryName(entry.country["spanish-name"]).includes(q)
      );
    });
  }, [countries, continent, query]);

  return (
    <Wrap>
      <Header>
        <Brand>Atlaxis</Brand>
        {/* Labelled with the language it switches *to*, so it reads as the
            way out for someone who can't read the current one. */}
        <GhostButton
          type="button"
          onClick={onSwitchLanguage}
          aria-label={switchLanguageAria(language)}
        >
          <span aria-hidden="true">🌐</span> {switchLanguageLabel(language)}
        </GhostButton>
      </Header>

      <label htmlFor="mobile-country-search">
        <ListCount as="span">{t.searchLabel}</ListCount>
      </label>
      <SearchInput
        id="mobile-country-search"
        type="search"
        value={query}
        placeholder={t.searchPlaceholder}
        onChange={(e) => setQuery(e.target.value)}
      />

      <ChipsRow role="group" aria-label={t.allContinents}>
        <Chip
          type="button"
          $active={!continent}
          aria-pressed={!continent}
          onClick={() => setContinent(undefined)}
        >
          {t.allContinents}
        </Chip>
        {FILTERABLE_CONTINENTS.map((option) => (
          <Chip
            key={option}
            type="button"
            $active={continent === option}
            aria-pressed={continent === option}
            onClick={() =>
              setContinent((current) =>
                current === option ? undefined : option
              )
            }
          >
            {continentLabels[option]}
          </Chip>
        ))}
      </ChipsRow>

      <MapPanel>
        <WorldMap
          data={data}
          language={language}
          focusedContinent={continent}
          showControls={false}
          fillHeight
        />
      </MapPanel>

      <Hint>{t.mapHint}</Hint>

      <ListCount>{t.countriesFound(visibleCountries.length)}</ListCount>

      {visibleCountries.length === 0 ? (
        <Empty>{t.noMatches}</Empty>
      ) : (
        <CountryList>
          {visibleCountries.map(({ country, displayName }) => (
            <li key={country["english-name"]}>
              <CountryRow to={countryHref(isSpanish, displayName)}>
                <RowFlag
                  src={`https://flagcdn.com/w40/${country.initials.toLowerCase()}.png`}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                {displayName}
              </CountryRow>
            </li>
          ))}
        </CountryList>
      )}

      <FooterNav>
        <NavLink to={learnHref(isSpanish)}>{t.learn}</NavLink>
        <NavLink to="/autotest">{t.quiz}</NavLink>
      </FooterNav>
    </Wrap>
  );
};
